import { memo } from 'react';
import type { SpacingFootprint, SpacingIssue } from '@peles/shared';
import { ftToPx } from './helpers';

export interface SpacingOverlayProps {
  issues: SpacingIssue[];
  footprints: SpacingFootprint[];
}

const COLORS = { overlap: '#dc2626', tight: '#d97706' } as const;

/**
 * Outlines the items in each spacing problem on the canvas — red where two
 * overlap, amber where they are closer than an aisle. Drawn over the items
 * and never in the way of a click.
 */
export const SpacingOverlay = memo(function SpacingOverlay({
  issues,
  footprints,
}: SpacingOverlayProps) {
  if (issues.length === 0) return null;
  // An item in both an overlap and a tight pair is marked as the worse.
  const worst = new Map<string, SpacingIssue['kind']>();
  for (const issue of issues) {
    for (const key of [issue.a, issue.b]) {
      if (worst.get(key) !== 'overlap') worst.set(key, issue.kind);
    }
  }
  return (
    <g pointerEvents="none" data-testid="spacing-overlay">
      {footprints.map((f, i) => {
        const kind = worst.get(f.key);
        if (!kind) return null;
        return (
          <polygon
            key={`${f.key}-${i}`}
            points={f.points.map((p) => `${ftToPx(p.x)},${ftToPx(p.y)}`).join(' ')}
            fill={COLORS[kind]}
            fillOpacity={0.12}
            stroke={COLORS[kind]}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            data-kind={kind}
          />
        );
      })}
    </g>
  );
});
